import { Button, styled, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getGlobalSearchResults } from '../../redux/actions/globalSearchActions';
import { selectGlobalSearch } from '../../redux/reducers/globalSearchReducer';
import { AppDispatch } from '../../redux/store';
import { Conversation } from '../../Types/conversation';
import { CardSize } from '../../Types/enums';
import { Folder } from '../../Types/folder';
import ChatCard from '../Chat/ChatCard';
import FolderCard from '../Folder/FolderCard';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

const StyledList = styled('div')<{ condensedView: boolean }>(({ condensedView }) => ({
  display: 'flex',
  flexDirection: condensedView ? 'column' : 'row',
  flexWrap: condensedView ? 'nowrap' : 'wrap',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'flex-start',
  gap: '16px',
  paddingTop: 0,
}));

const LoadMoreButton = styled(Button)({
  margin: '8px 16px',
  color: '#50658E',
  fontWeight: '400',
  fontSize: '16px',
});

interface FolderListProps {
  onConversationUpdated: (
    conversationId?: string,
    folderId?: string,
    shouldFetchConversations?: boolean
  ) => void;
  onFolderCardClick: (folder: Folder) => void;
  condensedView: boolean;
}

const GlobalSearchResults = ({
  onConversationUpdated,
  onFolderCardClick,
  condensedView,
}: FolderListProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    conversationsOnly,
    searchQuery,
    conversations,
    folders,
    currentCount,
    totalCount,
    searchSelected,
  } = useSelector(selectGlobalSearch);
  const analytics = useAnalytics();
  const [shouldShowLoadMoreButton, setShouldShowLoadMoreButton] = useState<boolean>(false);

  useEffect(() => {
    setShouldShowLoadMoreButton(currentCount > 0 && currentCount < totalCount);
  }, [conversationsOnly, currentCount, totalCount]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <div
        style={{
          height: 'calc(100vh - 300px)',
          overflowY: 'auto',
          margin: '8px 0',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {folders.length + conversations.length > 0 ? (
          <>
            <StyledList condensedView={condensedView}>
              {folders.map((result) => (
                <div key={result.id}>
                  <FolderCard
                    IsSearchFocused={searchSelected}
                    searchQuery={searchQuery}
                    folder={result}
                    onFolderCardClick={() => onFolderCardClick(result as Folder)}
                    size={condensedView ? CardSize.SMALL : CardSize.MEDIUM}
                  />
                </div>
              ))}
              {conversations.map((result) => (
                <div key={result.id}>
                  <ChatCard
                    IsSearchFocused={searchSelected}
                    searchQuery={searchQuery}
                    conversation={result as Conversation}
                    onConversationCardClick={() =>
                      onConversationUpdated(result.id, result.folderId, true)
                    }
                    size={condensedView ? CardSize.SMALL : CardSize.MEDIUM}
                  />
                </div>
              ))}
            </StyledList>
            {shouldShowLoadMoreButton && (
              <LoadMoreButton
                variant="text"
                onClick={() => {
                  dispatch(getGlobalSearchResults({ pageAction: 'increment' }));
                  analytics.event(AnalyticsEvent.WM_NIGEL_LOAD_MORE_SEARCH_RESULTS);
                }}
              >
                + Load More
              </LoadMoreButton>
            )}
          </>
        ) : (
          <Typography variant="body1" sx={{ margin: '16px', color: '#757575' }}>
            No results found, try a new search
          </Typography>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchResults;
